import { useState, type FormEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import { useDeviceInventoryDetail, useUpdateDeviceInventory } from "@/hooks/use-device-inventory";
import {
  PageHeader,
  Card,
  CardHeader,
  CardContent,
  Skeleton,
  Button,
  FormField,
  inputClass,
} from "@/components/ui";

type DeviceInventoryUpdate = components["schemas"]["DeviceInventoryUpdate"];

export const Route = createFileRoute("/_authenticated/temporary-devices/$id/edit")({
  component: DeviceInventoryEditPage,
});

/**
 * Edit-route covering the 12 EWAW + 1 ENW inventory fields per
 * `docs/contracts/backflow-device-editability.md` inventory matrix rows 13-25.
 * The 8 IUR identity fields + 2 INEE (`first_seen_at`,
 * `approval_status_snapshot_at_first_install`) + 2 RFO (`retired_at`,
 * `retirement_reason` + `retirement_notes`) are NOT mutated here — those
 * route through the inventory-replacement Replace flow or the dedicated
 * Retire endpoint per AWWA M14 §6.5.
 *
 * Lifecycle guard: retired inventory rows render a non-mountable warning
 * Card with regulatory citation; the form never renders. Callers lacking the
 * `device_inventory:update` grant are redirected back to the detail page
 * (hide-not-disable).
 */
function DeviceInventoryEditPage() {
  const { id } = Route.useParams();
  const { t } = useTranslation(["device-inventory", "org"]);
  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  const canUpdate = can("device_inventory", "update");
  const navigate = useNavigate();

  const detail = useDeviceInventoryDetail(id);
  const update = useUpdateDeviceInventory(id);

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  if (!canUpdate) {
    return (
      <div className="p-8">
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-on-surface">
              {t("device-inventory:edit.notPermittedTitle")}
            </h2>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-on-surface">{t("device-inventory:edit.notPermittedBody")}</p>
            <div className="mt-4">
              <Button
                variant="secondary"
                onClick={() => void navigate({ to: "/temporary-devices/$id", params: { id } })}
              >
                {t("device-inventory:edit.backToDetail")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (detail.isLoading) {
    return (
      <div className="p-8">
        <Skeleton className="h-10 w-80" />
        <div className="mt-6 space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="p-8">
        <p className="text-danger">{t("device-inventory:errors.loadDetailFailed")}</p>
      </div>
    );
  }

  const row = detail.data;

  // AWWA M14 §6.5: a retired inventory row is a historical regulatory
  // artifact. Edits route through the Replace flow that mints a successor
  // row, never PATCH on the retired row.
  if (row.retired_at) {
    return (
      <div className="p-8">
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-on-surface">
              {t("device-inventory:edit.retiredTitle")}
            </h2>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-on-surface">{t("device-inventory:edit.retiredBody")}</p>
            <div className="mt-4">
              <Button
                variant="secondary"
                onClick={() => void navigate({ to: "/temporary-devices/$id", params: { id } })}
              >
                {t("device-inventory:edit.backToDetail")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <EditForm
      row={row}
      onSuccess={() => {
        void navigate({ to: "/temporary-devices/$id", params: { id } });
      }}
      update={update}
    />
  );
}

interface EditFormProps {
  row: components["schemas"]["DeviceInventory"];
  onSuccess: () => void;
  update: ReturnType<typeof useUpdateDeviceInventory>;
}

function EditForm({ row, onSuccess, update }: EditFormProps) {
  const { t } = useTranslation(["device-inventory"]);

  const [manufacturerFreetext, setManufacturerFreetext] = useState<string>(
    row.manufacturer_freetext ?? "",
  );
  const [modelFreetext, setModelFreetext] = useState<string>(row.model_freetext ?? "");
  const [bidirectionalCapable, setBidirectionalCapable] = useState<boolean>(
    row.bidirectional_capable ?? false,
  );
  const [leadFreeVerified, setLeadFreeVerified] = useState<boolean>(
    row.lead_free_verified_per_nsf372 ?? false,
  );
  const [altBody, setAltBody] = useState<string>(row.alternate_listing_body ?? "");
  const [altStandard, setAltStandard] = useState<string>(row.alternate_listing_standard ?? "");
  const [altId, setAltId] = useState<string>(row.alternate_listing_id_or_file_number ?? "");
  const [stateApprovalJurisdiction, setStateApprovalJurisdiction] = useState<string>(
    row.state_approval_jurisdiction ?? "",
  );
  const [cycleYears, setCycleYears] = useState<string>(
    row.non_testable_replacement_cycle_years !== null &&
      row.non_testable_replacement_cycle_years !== undefined
      ? String(row.non_testable_replacement_cycle_years)
      : "",
  );
  const [lastReplacementAt, setLastReplacementAt] = useState<string>(
    row.non_testable_last_replacement_at ?? "",
  );

  const isAltListed = row.listing_source === "alt_listed";

  // Identity editability split per AWWA M14 §6 + `backflow-device-editability.md`
  // inventory matrix: manufacturer + model are part of the physical device's
  // regulatory identity. A catalog-anchored device (manufacturer_id present /
  // catalog_assembly_listing_id present) carries that identity in the catalog
  // FKs; the freetext columns are NULL and IMMUTABLE-USE-REPLACE. An
  // unlisted/freetext device captures them as editable freetext (EWAW) and the
  // manufacturer side is REQUIRED — the DB chk_manufacturer_xor would fail if
  // both manufacturer_id and manufacturer_freetext were null.
  const manufacturerIsCatalogAnchored = !!row.manufacturer_id;
  const modelIsCatalogAnchored = !!row.catalog_assembly_listing_id;

  function nullableTrim(v: string): string | null {
    const t = v.trim();
    return t.length === 0 ? null : t;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const body: DeviceInventoryUpdate = {
      // Catalog-anchored devices carry their identity in the catalog FKs; the
      // freetext columns are dropped from the PATCH so they stay NULL (the
      // server enforces chk_manufacturer_xor + ErrModelFreetextRedundantWith-
      // Listing). Unlisted/freetext devices send the edited freetext.
      ...(manufacturerIsCatalogAnchored
        ? {}
        : { manufacturer_freetext: nullableTrim(manufacturerFreetext) }),
      ...(modelIsCatalogAnchored ? {} : { model_freetext: nullableTrim(modelFreetext) }),
      bidirectional_capable: bidirectionalCapable,
      lead_free_verified_per_nsf372: leadFreeVerified,
      alternate_listing_body: isAltListed ? nullableTrim(altBody) : null,
      alternate_listing_standard: isAltListed ? nullableTrim(altStandard) : null,
      alternate_listing_id_or_file_number: isAltListed ? nullableTrim(altId) : null,
      state_approval_jurisdiction: nullableTrim(stateApprovalJurisdiction),
      non_testable_replacement_cycle_years:
        cycleYears.trim().length === 0 ? null : Number(cycleYears),
      non_testable_last_replacement_at: nullableTrim(lastReplacementAt),
    };
    update.mutate(body, {
      onSuccess: () => {
        onSuccess();
      },
    });
  }

  return (
    <div className="p-8">
      <PageHeader title={t("device-inventory:edit.title")} subtitle={row.serial_number} />

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        <Card>
          <CardHeader>
            <h2 className="text-base font-semibold text-on-surface">
              {t("device-inventory:edit.sectionIdentityRefinement")}
            </h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {manufacturerIsCatalogAnchored ? (
                <FormField label={t("device-inventory:fields.manufacturer")}>
                  <p className="text-sm text-on-surface">{row.manufacturer_label}</p>
                </FormField>
              ) : (
                <FormField label={t("device-inventory:fields.manufacturerFreetext")} required>
                  <input
                    type="text"
                    required
                    className={inputClass}
                    value={manufacturerFreetext}
                    onChange={(e) => setManufacturerFreetext(e.target.value)}
                  />
                </FormField>
              )}
              {modelIsCatalogAnchored ? (
                <FormField label={t("device-inventory:fields.model")}>
                  <p className="text-sm text-on-surface">{row.model_label}</p>
                </FormField>
              ) : (
                <FormField label={t("device-inventory:fields.model")}>
                  <input
                    type="text"
                    className={inputClass}
                    value={modelFreetext}
                    onChange={(e) => setModelFreetext(e.target.value)}
                  />
                </FormField>
              )}
              <FormField label={t("device-inventory:fields.stateApprovalJurisdiction")}>
                <input
                  type="text"
                  className={inputClass}
                  value={stateApprovalJurisdiction}
                  onChange={(e) => setStateApprovalJurisdiction(e.target.value)}
                />
              </FormField>
            </div>
            {(manufacturerIsCatalogAnchored || modelIsCatalogAnchored) && (
              <p className="mt-4 text-sm text-muted">
                {t("device-inventory:edit.identityCatalogAnchoredAffordance")}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-base font-semibold text-on-surface">
              {t("device-inventory:edit.sectionCapability")}
            </h2>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-on-surface">
                <input
                  type="checkbox"
                  checked={bidirectionalCapable}
                  onChange={(e) => setBidirectionalCapable(e.target.checked)}
                />
                {t("device-inventory:fields.bidirectionalCapable")}
              </label>
              <label className="flex items-center gap-2 text-sm text-on-surface">
                <input
                  type="checkbox"
                  checked={leadFreeVerified}
                  onChange={(e) => setLeadFreeVerified(e.target.checked)}
                />
                {t("device-inventory:fields.leadFreeVerifiedPerNsf372")}
              </label>
            </div>
          </CardContent>
        </Card>

        {isAltListed && (
          <Card>
            <CardHeader>
              <h2 className="text-base font-semibold text-on-surface">
                {t("device-inventory:edit.sectionAltListing")}
              </h2>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label={t("device-inventory:fields.alternateListingBody")}>
                  <input
                    type="text"
                    className={inputClass}
                    value={altBody}
                    onChange={(e) => setAltBody(e.target.value)}
                  />
                </FormField>
                <FormField label={t("device-inventory:fields.alternateListingStandard")}>
                  <input
                    type="text"
                    className={inputClass}
                    value={altStandard}
                    onChange={(e) => setAltStandard(e.target.value)}
                  />
                </FormField>
                <FormField label={t("device-inventory:fields.alternateListingIdOrFileNumber")}>
                  <input
                    type="text"
                    className={inputClass}
                    value={altId}
                    onChange={(e) => setAltId(e.target.value)}
                  />
                </FormField>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <h2 className="text-base font-semibold text-on-surface">
              {t("device-inventory:edit.sectionMaintenance")}
            </h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField label={t("device-inventory:fields.nonTestableReplacementCycleYears")}>
                <input
                  type="number"
                  min="0"
                  className={inputClass}
                  value={cycleYears}
                  onChange={(e) => setCycleYears(e.target.value)}
                />
              </FormField>
              <FormField label={t("device-inventory:fields.nonTestableLastReplacementAt")}>
                <input
                  type="datetime-local"
                  className={inputClass}
                  value={lastReplacementAt}
                  onChange={(e) => setLastReplacementAt(e.target.value)}
                />
              </FormField>
            </div>
          </CardContent>
        </Card>

        {update.isError && (
          <p className="text-danger">
            {(update.error as Error).message || t("device-inventory:edit.submitError")}
          </p>
        )}

        <div className="flex gap-2">
          <Button type="submit" disabled={update.isPending}>
            {t("device-inventory:edit.submit")}
          </Button>
        </div>
      </form>
    </div>
  );
}
